/**
 * Per-pathway dossier progress-stepper copy.
 *
 * The steps shown while a dossier generates are pathway-specific — US 510(k)
 * is about predicate + substantial equivalence; EU MDR/IVDR is about EU
 * databases, GSPR, and conformity assessment. Instead of one hardcoded US set,
 * this is a small registry of pathways with a resolver.
 *
 * SoC + extensibility: the stepper component stays generic (no `if EU`). A new
 * country/pathway adds an entry here; the resolver maps the run to it. Today
 * the mapping is country-keyed (US 510(k) vs EU device) because that is 1:1;
 * when finer pathways land (US De Novo / PMA, EU drug-CTD), extend the resolver
 * to read submission_type / document_type — the registry shape is ready. An
 * unrecognised country falls back to NEUTRAL copy, never another country's steps.
 */

import { EU_MEDICINAL_LEGAL_BASES } from './eu-submission-pathways';

export interface WorkflowCopy {
  /** Stepper pills, in order. */
  steps: readonly string[];
  /** Page subtitle for this pathway (kept here so copy lives in one place). */
  subtitle: string;
}

const US_510K: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query regulatory databases for predicate context',
    'Draft substantial-equivalence comparison',
    'Compose dossier sections',
    'Finalize submission dossier',
  ],
  subtitle:
    'A complete US FDA 510(k) submission dossier for this product — predicate ' +
    'comparison, technological characteristics, performance data, labeling, and ' +
    'substantial-equivalence analysis — assembled by the REGARA AI agent.',
};

// PENDING RA REVIEW — every US pathway other than 510(k). Before these, every US
// run showed the 510(k) predicate / substantial-equivalence copy.
const US_DENOVO: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query regulatory databases for classification context',
    'Draft the risk-based classification and special controls',
    'Compose dossier sections',
    'Finalize the De Novo dossier',
  ],
  subtitle:
    'A complete US FDA De Novo classification request for this product — device ' +
    'description, risk-based classification, proposed special controls, performance ' +
    'data, labeling and benefit-risk analysis — assembled by the REGARA AI agent.',
};

const US_PMA: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query regulatory databases for device context',
    'Draft the safety and effectiveness evidence',
    'Compose the 21 CFR 814.20 sections',
    'Finalize the PMA dossier',
  ],
  subtitle:
    'A complete US FDA PMA (premarket approval) dossier for this product — device ' +
    'description, nonclinical and clinical evidence of safety and effectiveness, ' +
    'manufacturing, and labeling — assembled by the REGARA AI agent.',
};

const US_510K_EXEMPT: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Confirm the exemption in the FDA classification regulation',
    'Draft the technical documentation',
    'Compose dossier sections',
    'Finalize the exempt-device dossier',
  ],
  subtitle:
    'A US FDA technical documentation package for this 510(k)-exempt device — ' +
    'classification and exemption basis, device description, quality-system and ' +
    'labeling — assembled by the REGARA AI agent.',
};

const US_NDA: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query FDA drug databases',
    'Draft the quality, nonclinical and clinical summaries',
    'Compose the eCTD modules',
    'Finalize the NDA dossier',
  ],
  subtitle:
    'A complete US FDA NDA (505(b)(1)) submission dossier for this product — ' +
    'quality (CMC), nonclinical and clinical evidence of safety and effectiveness, ' +
    'and labeling — assembled by the REGARA AI agent.',
};

const US_NDA_505B2: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query FDA drug databases for the listed drug and literature',
    'Draft the bridging and reliance sections',
    'Compose the eCTD modules',
    'Finalize the 505(b)(2) dossier',
  ],
  subtitle:
    'A complete US FDA 505(b)(2) NDA submission dossier for this product — the ' +
    'studies or listed drug relied on, bridging data, quality (CMC), clinical ' +
    'evidence and labeling — assembled by the REGARA AI agent.',
};

const US_BLA: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query FDA biologics databases',
    'Draft the characterization, nonclinical and clinical summaries',
    'Compose the eCTD modules',
    'Finalize the BLA dossier',
  ],
  subtitle:
    'A complete US FDA BLA (351(a)) submission dossier for this product — quality ' +
    '(CMC) and characterization, nonclinical and clinical evidence of safety, purity ' +
    'and potency, and labeling — assembled by the REGARA AI agent.',
};

const US_ANDA: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Confirm the Reference Listed Drug in FDA records',
    'Draft the pharmaceutical-equivalence and bioequivalence sections',
    'Compose the eCTD modules',
    'Finalize the ANDA dossier',
  ],
  subtitle:
    'A complete US FDA ANDA (505(j)) submission dossier for this product — the ' +
    'Reference Listed Drug basis, pharmaceutical equivalence, bioequivalence, ' +
    'quality (CMC) and labeling — assembled by the REGARA AI agent.',
};

const US_BIOSIMILAR: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Confirm the reference product in FDA records',
    'Draft the biosimilarity sections (analytical, PK and clinical)',
    'Compose the eCTD modules',
    'Finalize the 351(k) dossier',
  ],
  subtitle:
    'A complete US FDA 351(k) biosimilar submission dossier for this product — the ' +
    'reference product, analytical similarity, comparative PK and clinical data, ' +
    'quality (CMC) and labeling — assembled by the REGARA AI agent.',
};

/** US pathways whose copy is not the 510(k) default, by submission_type. */
const US_BY_SUBMISSION_TYPE: Record<string, WorkflowCopy> = {
  'denovo-no-clinical': US_DENOVO,
  'denovo-clinical': US_DENOVO,
  pma: US_PMA,
  '510k-exempt': US_510K_EXEMPT,
  nda: US_NDA,
  'nda-505b2': US_NDA_505B2,
  bla: US_BLA,
  anda: US_ANDA,
  biosimilar: US_BIOSIMILAR,
};

const EU_DEVICE: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query EU regulatory databases (EUR-Lex / MDCG / EUDAMED)',
    'Map GSPR & conformity-assessment requirements',
    'Compose the dossier annexes',
    'Finalise the EU dossier',
  ],
  subtitle:
    'A complete EU MDR/IVDR submission dossier for this product — GSPR ' +
    'conformity, technological characteristics, performance data, labeling, and ' +
    'clinical/performance evaluation — assembled by the REGARA AI agent.',
};

// PENDING RA REVIEW — EU medicinal products (drugs and biologics), the eu_medicinal
// CTD dossier. Before this, they showed the EU MDR/IVDR device copy above.
const EU_MEDICINAL: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query EU regulatory sources (EUR-Lex / EMA)',
    'Map the legal basis and CTD requirements',
    'Compose the CTD modules',
    'Finalise the EU marketing-authorisation dossier',
  ],
  subtitle:
    'A complete EU marketing-authorisation (CTD) dossier for this medicinal product ' +
    'under Directive 2001/83/EC — the legal basis, quality (CMC), nonclinical and ' +
    'clinical evidence, and product information — assembled by the REGARA AI agent.',
};

const CA_MD: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query Health Canada databases (MDALL / recalls / advisories)',
    'Map SOR/98-282 classification + IMDRF ToC requirements',
    'Compose the 6-chapter dossier',
    'Finalise the Health Canada MDL dossier',
  ],
  subtitle:
    'A complete Health Canada medical-device licence (MDL) dossier — SOR/98-282 ' +
    'classification, IMDRF Table of Contents, device/performance evidence, labelling, ' +
    'and ISO 13485/MDSAP — assembled by the REGARA AI agent.',
};

const UK_NI_IVDR: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query UK/EU regulatory databases (EUR-Lex / MDCG / EUDAMED)',
    'Map GSPR & CE-UKNI conformity-assessment requirements',
    'Compose the NI IVDR annexes',
    'Finalise the Northern Ireland (Windsor) dossier',
  ],
  subtitle:
    'A complete UK (Northern Ireland) IVDR submission dossier under EU IVDR 2017/746 (Windsor Framework) — GSPR conformity, performance data, labelling, and performance evaluation — assembled by the REGARA AI agent.',
};

const UK_NI_MDR: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query UK/EU regulatory databases (EUR-Lex / MDCG / EUDAMED)',
    'Map GSPR & CE-UKNI conformity-assessment requirements',
    'Compose the NI MDR Annex II Technical Documentation',
    'Finalise the Northern Ireland (Windsor) MDR dossier',
  ],
  subtitle:
    'A complete UK (Northern Ireland) MDR submission dossier under EU MDR 2017/745 (Windsor Framework) — GSPR conformity, technical documentation, labelling, and clinical evaluation — assembled by the REGARA AI agent.',
};


const UK_MD: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query MHRA regulatory databases (gov.uk / MHRA guidance)',
    'Map UK MDR 2002 essential requirements + UKCA conformity route',
    'Compose the 6-chapter Technical File',
    'Finalise the UKCA submission dossier',
  ],
  subtitle:
    'A complete UK (Great Britain) UKCA Technical Documentation dossier under ' +
    'UK MDR 2002 (SI 2002/618) — essential requirements, UK Approved Body ' +
    'conformity assessment, device evidence, labelling, and ISO 13485 — ' +
    'assembled by the REGARA AI agent.',
};

const UK_GB_IVD: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query MHRA regulatory databases (gov.uk IVD designated standards)',
    'Map UK MDR 2002 Part IV essential requirements + UKCA IVD conformity route',
    'Compose the 6-chapter IVD Technical File (analytical + clinical performance)',
    'Finalise the UKCA IVD submission dossier',
  ],
  subtitle:
    'A complete UK (Great Britain) UKCA IVD Technical Documentation dossier under ' +
    'UK MDR 2002 Part IV (retained IVDD) — IVD essential requirements, analytical ' +
    'performance, clinical performance / scientific validity, and ISO 13485 — ' +
    'assembled by the REGARA AI agent.',
};

const GENERIC: WorkflowCopy = {
  steps: [
    'Read product specifications',
    'Pull customer + product details from REGARA',
    'Query the relevant regulatory databases',
    'Draft the submission dossier',
    'Compose the dossier sections',
    'Finalise the submission dossier',
  ],
  subtitle:
    'A complete regulatory submission dossier for this product — technological ' +
    'characteristics, performance data, labeling, and the target-market ' +
    'regulatory analysis — assembled by the REGARA AI agent.',
};

/** Per-country stepper/subtitle copy. Adding a country is one entry here; an
 *  unrecognised country falls back to NEUTRAL copy, never another country's. */
const WORKFLOW_BY_COUNTRY: Record<string, WorkflowCopy> = {
  US: US_510K,
  EU: EU_DEVICE,
  CA: CA_MD,
  NI: UK_NI_IVDR,
  UK: UK_MD,
};

/** Resolve the stepper/subtitle copy for a GB IVD run (UK MDR 2002 Part IV).
 *  Used when the result page detects a GB IVD submission_type or document_type.
 *  Falls back to UK_MD (GB device) for unrecognised values. */
export const UK_GB_IVD_WORKFLOW: WorkflowCopy = UK_GB_IVD;

/**
 * Resolve the stepper/subtitle copy for a run by its target market.
 * `null`/`undefined` (the dispatch form, before a country is chosen) and any
 * country without a tailored set return the neutral copy.
 */
export function workflowForCountry(country: string | null | undefined): WorkflowCopy {
  return WORKFLOW_BY_COUNTRY[(country ?? '').toUpperCase()] ?? GENERIC;
}

/** Resolve workflow copy for a run object, preferring IVD-specific copy for
 *  GB IVD runs (UK MDR 2002 Part IV) over the generic UK device copy, and
 *  MDR-specific copy for NI MDR runs (ni-class-*) over the NI IVDR default. */
export function workflowForRun(run: {
  country?: string | null;
  submission_type?: string | null;
  document_type?: string | null;
} | null | undefined): WorkflowCopy {
  if (!run) return GENERIC;
  const subType = run.submission_type ?? '';
  const docType = run.document_type ?? '';
  if (
    GB_IVD_SUBMISSION_TYPES.has(subType) ||
    docType.startsWith('uk_gb_ivdr_')
  ) {
    return UK_GB_IVD;
  }
  // NI MDR device runs: canonical submission_type is IIa/IIb/IIb_implantable/III.
  // NI IVDR runs: canonical submission_type is A_sterile/B/C/D.
  // WORKFLOW_BY_COUNTRY defaults NI → NI IVDR copy, so we override for MDR class.
  if ((run.country ?? '').toUpperCase() === 'NI' && NI_MDR_CANONICAL_CLASSES.has(subType)) {
    return UK_NI_MDR;
  }
  if ((run.country ?? '').toUpperCase() === 'US' && US_BY_SUBMISSION_TYPE[subType]) {
    return US_BY_SUBMISSION_TYPE[subType];
  }
  // EU drug / biologic: the CTD document type, or one of its legal bases.
  if (
    (run.country ?? '').toUpperCase() === 'EU' &&
    (docType === 'eu_ctd' || EU_MEDICINAL_LEGAL_BASES.has(subType))
  ) {
    return EU_MEDICINAL;
  }
  return workflowForCountry(run.country);
}

// PENDING RA REVIEW — the dossier dispatch page's subtitle before any run, by
// product type (the pathway is chosen in the form below it). A type without one
// (combination, unknown) keeps the neutral GENERIC subtitle.
const DISPATCH_SUBTITLE_BY_PRODUCT_TYPE: Record<string, string> = {
  device:
    'A complete regulatory submission dossier for this medical device — device ' +
    'description, technological characteristics, performance data, labeling, and the ' +
    'target-market regulatory analysis — assembled by the REGARA AI agent.',
  drug:
    'A complete regulatory submission dossier for this drug — the regulatory basis, ' +
    'quality (CMC), nonclinical and clinical evidence, labeling, and the target-market ' +
    'regulatory analysis — assembled by the REGARA AI agent.',
  biologic:
    'A complete regulatory submission dossier for this biologic — the regulatory ' +
    'basis, quality (CMC) and characterization, nonclinical and clinical evidence, ' +
    'labeling, and the target-market regulatory analysis — assembled by the REGARA AI agent.',
};

/** The dispatch page's subtitle for a product type (before any run exists). */
export function dispatchSubtitleFor(productType: string | null | undefined): string {
  return DISPATCH_SUBTITLE_BY_PRODUCT_TYPE[productType ?? ''] ?? GENERIC.subtitle;
}

/** GB IVD submission_type values — duplicated locally so workflowSteps.ts
 *  has no cross-feature import cycle (uk-submission-pathways imports nothing
 *  from here, and we keep this file self-contained).
 *  General IVD is self-declared (no submission) and is intentionally excluded. */
const GB_IVD_SUBMISSION_TYPES = new Set([
  'gb-ivd-self-test',
  'gb-ivd-list-b',
  'gb-ivd-list-a',
]);

/** Canonical NI MDR device classes (as normalised by the BE dispatch policy).
 *  Used to pick MDR-specific stepper copy for NI runs that are NOT IVDR. */
const NI_MDR_CANONICAL_CLASSES = new Set(['IIa', 'IIb', 'IIb_implantable', 'III']);
